import React, { useState, useCallback } from 'react'
import { Snackbar, Alert } from '@mui/material'
import type { SnackbarSeverity } from './types'

export interface ContentDetailSnackbarProps {
  open: boolean
  message: string
  severity: SnackbarSeverity
  onClose: () => void
}

/**
 * Presentational snackbar for content detail pages.
 * Position and behavior match the shared pattern (bottom-right, 6s auto-hide).
 */
function ContentDetailSnackbar ({ open, message, severity, onClose }: ContentDetailSnackbarProps) {
  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  )
}

export interface UseContentDetailSnackbarReturn {
  /** Current snackbar state (open, message, severity) */
  snackbar: { open: boolean; message: string; severity: SnackbarSeverity }
  /** Show a message (opens snackbar with given message and severity) */
  showSnackbar: (message: string, severity: SnackbarSeverity) => void
  /** Close the snackbar */
  handleClose: () => void
}

/**
 * Hook for snackbar state and actions on content detail pages.
 * Use with <ContentDetailSnackbar /> for the UI.
 */
function useContentDetailSnackbar (): UseContentDetailSnackbarReturn {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as SnackbarSeverity,
  })

  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity) => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const handleClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  return { snackbar, showSnackbar, handleClose }
}

export { useContentDetailSnackbar }
export default ContentDetailSnackbar
